/**
 * Weather (BUILDLIST 62.18; the owner, Oct 9: "another nice depth lever, just depends on if it's
 * justifiable from a cost perspective"). Not a fluid simulation: each planet has a **seeded
 * timeline** of weather segments drawn from its biome's table — a few numbers per segment, so it is
 * free to step, identical on every engine, and forecastable (a forecast is knowledge: attack in the
 * fog, wait out the storm).
 *
 * Step 1 is **visibility**: every kind has a sight range (in person-lengths, so it scales with the
 * profile) and multipliers on perception and hearing. Those read through existing dials — the
 * guards' perception range, the action bot's sight, hearing — and nothing else. Wind on lobbed
 * shells is its own decision (DECISIONS 114). Off unless `GameConfig.weather` is set: every match
 * without it is unchanged.
 *
 * A segment is `WEATHER_SEGMENT_S` seconds on the action clock and one round on the turn clock
 * (until decision 110 fixes seconds per turn).
 */
import type { GameState, PlanetArchetype, Vec3 } from '../types';
import { SeededRandom } from './random';
import { G } from '../constants';
import { dcos, dsin } from './dmath';
import { cellWeather, cellWeatherEffect } from './weatherCells';

export type WeatherKind = 'clear' | 'haze' | 'rain' | 'fog' | 'storm' | 'dust' | 'ash';

export interface WeatherEffect {
  /** How far a person sees, in person-lengths (Infinity = the horizon and terrain decide). */
  sight: number;
  /** Decision 120: the wind's push on a lobbed shell, as a share of the planet's surface gravity (0 = calm). */
  wind: number;
  /** × the guards' perception range (turn clock) and the bots' model of it. */
  perception: number;
  /** × hearing range. */
  hearing: number;
}

export const WEATHER_EFFECTS: Record<WeatherKind, WeatherEffect> = {
  clear: { sight: Infinity, perception: 1, hearing: 1, wind: 0 },
  haze: { sight: 60, perception: 0.85, hearing: 1, wind: 0.04 },
  rain: { sight: 35, perception: 0.75, hearing: 0.7, wind: 0.10 },
  fog: { sight: 14, perception: 0.5, hearing: 1, wind: 0.02 },
  storm: { sight: 20, perception: 0.6, hearing: 0.5, wind: 0.25 },
  dust: { sight: 18, perception: 0.6, hearing: 0.85, wind: 0.18 },
  ash: { sight: 22, perception: 0.65, hearing: 0.9, wind: 0.08 },
};

/** Each biome's weather: the share of time each kind holds (its stationary mix). */
export const BIOME_WEATHER: Record<PlanetArchetype, Partial<Record<WeatherKind, number>>> = {
  thalia: { clear: 0.5, haze: 0.2, rain: 0.2, fog: 0.1 },
  bruma: { clear: 0.2, haze: 0.35, fog: 0.35, storm: 0.1 },
  tephra: { clear: 0.35, ash: 0.4, haze: 0.15, storm: 0.1 },
  murex: { clear: 0.3, haze: 0.3, fog: 0.4 },
  pallor: { clear: 0.55, haze: 0.2, dust: 0.25 },
  pelago: { clear: 0.35, rain: 0.35, fog: 0.15, storm: 0.15 },
  ochrus: { clear: 0.5, dust: 0.4, storm: 0.1 },
  viridis: { clear: 0.25, haze: 0.2, rain: 0.35, fog: 0.2 },
  sonora: { clear: 0.5, haze: 0.3, dust: 0.2 },
};

/** Seconds per weather segment on the action clock. */
export const WEATHER_SEGMENT_S = 120;
/** Chance a segment keeps the previous one's weather (weather comes in spells, not flickers). */
export const WEATHER_PERSISTENCE = 0.6;

const KINDS: WeatherKind[] = ['clear', 'haze', 'rain', 'fog', 'storm', 'dust', 'ash'];

function draw(mix: Partial<Record<WeatherKind, number>>, u: number): WeatherKind {
  let total = 0;
  for (const k of KINDS) total += mix[k] ?? 0;
  let x = u * total;
  for (const k of KINDS) { const w = mix[k] ?? 0; if (w <= 0) continue; if (x < w) return k; x -= w; }
  return 'clear';
}

/**
 * The weather in segment `segment` of a planet's timeline (`key` names the planet: the match seed
 * and the planet's id). Each segment keeps the last with WEATHER_PERSISTENCE, else draws afresh
 * from the biome's mix — keyed draws, so any segment is reproducible and the timeline is the same
 * however it is read. Cost: one draw per segment from the start (segments are minutes or rounds).
 */
export function weatherAt(key: string, archetype: PlanetArchetype | string | undefined, segment: number): WeatherKind {
  const mix = BIOME_WEATHER[archetype as PlanetArchetype] ?? BIOME_WEATHER.thalia;
  const n = Math.max(0, Math.floor(segment));
  // 62.54 step 3: the timeline is drawn once per planet and extended as later segments are asked (the wind field asks
  // for a few segments round the present on every query); the draws are exactly the loop's, in the same order
  const memoKey = `${key}|${archetype}`;
  let line = timelineMemo.get(memoKey);
  if (!line) { line = [draw(mix, new SeededRandom(`${key}-weather-0`).next())]; timelineMemo.set(memoKey, line); }
  for (let s = line.length; s <= n; s++) {
    const r = new SeededRandom(`${key}-weather-${s}`);
    line.push(r.next() >= WEATHER_PERSISTENCE ? draw(mix, r.next()) : line[s - 1]);
  }
  return line[n];
}
/** The timelines drawn so far, per planet key and archetype (pure derived data; bounded by the planets and segments asked). */
const timelineMemo = new Map<string, WeatherKind[]>();

/** The next `count` segments from `segment` on — a forecast. */
export function forecast(key: string, archetype: PlanetArchetype | string | undefined, segment: number, count: number): WeatherKind[] {
  const out: WeatherKind[] = [];
  for (let i = 0; i < count; i++) out.push(weatherAt(key, archetype, segment + i));
  return out;
}

/** Which segment a match is in: minutes on the action clock, rounds on the turn clock. */
export function weatherSegment(state: Pick<GameState, 'config' | 'turnNumber'>, actionSeconds = 0): number {
  return state.config.clock === 'action' ? Math.floor(actionSeconds / (state.config.weatherSegmentS ?? WEATHER_SEGMENT_S)) : state.turnNumber;
}

/** A planet's weather now, or null when the match has no weather. */
export function planetWeather(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, actionSeconds = 0): WeatherKind | null {
  if (!state.config.weather) return null;
  const p = state.planets[planetIndex];
  if (!p) return null;
  if (state.config.weatherFixed) return state.config.weatherFixed;   // X6: the sector's window holds for the battle
  return weatherAt(`${state.config.seed}-${p.id}`, p.archetype, weatherSegment(state, actionSeconds));
}

/**
 * 62.19 (d): the next `count` segments on a planet, this one first — the forecast the player sees. Null when the match
 * has no weather; the sector's fixed window (X6) repeats, since it holds for the whole battle.
 */
export function planetForecast(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, count: number, actionSeconds = 0): WeatherKind[] | null {
  if (!state.config.weather) return null;
  const p = state.planets[planetIndex];
  if (!p) return null;
  if (state.config.weatherFixed) return Array.from({ length: count }, () => state.config.weatherFixed!);
  return forecast(`${state.config.seed}-${p.id}`, p.archetype, weatherSegment(state, actionSeconds), count);
}

/**
 * Decision 120: the wind over a planet now — a world-space vector whose length is the weather's `wind` share of the
 * planet's surface gravity (G·M / r²), or null when the match has no wind or the air is calm. Its direction is drawn
 * once per weather segment from the planet's timeline key, so the same seed blows the same way however it is read;
 * the kernel applies it along the planet's tangent plane at the shell and fades it with height (lib.rs wind_accel).
 */
export function planetWind(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, actionSeconds = 0): Vec3 | null {
  if (!state.config.wind) return null;
  const p = state.planets[planetIndex];
  if (!p) return null;
  const share = weatherEffect(state, planetIndex, actionSeconds).wind;
  if (share <= 0) return null;
  const r = new SeededRandom(`${state.config.seed}-${p.id}-wind-${weatherSegment(state, actionSeconds)}`);
  const z = r.range(-1, 1), phi = r.range(0, 2 * Math.PI);   // a uniform direction on the sphere
  const s = Math.sqrt(1 - z * z);
  const speed = share * G * p.mass / (p.radius * p.radius);
  return { x: s * dcos(phi) * speed, y: s * dsin(phi) * speed, z: z * speed };
}

/** Every planet's wind now (null = calm), in planet order — what the kernel is given. */
export function windsNow(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, actionSeconds = 0): Array<Vec3 | null> {
  if (!state.config.wind) return [];
  return state.planets.map((_, i) => planetWind(state, i, actionSeconds));
}

/** The weather's effect on a planet now (clear when the match has no weather). */
export function weatherEffect(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, actionSeconds = 0): WeatherEffect {
  const w = planetWeather(state, planetIndex, actionSeconds);
  return WEATHER_EFFECTS[w ?? 'clear'];
}

/**
 * 62.54 step 1 (ARCHITECTURE §5.6 "62.54, the design"): the weather at a place on a planet — `dir` the place's direction
 * from the planet's centre. Today every place on a planet has the planet's weather (`planetWeather`), so this returns
 * exactly that; step 5 reads the weather cell overhead instead (behind `weatherCells`). Every reader of the weather at a
 * place asks here, so that step changes no reader. Null when the match has no weather.
 */
export function weatherHere(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, dir: Vec3, actionSeconds = 0): WeatherKind | null {
  // 62.54 step 5: with weatherCells, the cell overhead (a fixed window still holds everywhere)
  if (state.config.weather && state.config.weatherCells && !state.config.weatherFixed) {
    const c = cellWeather(state, planetIndex, dir, actionSeconds);
    return c.cover >= 0.5 ? c.kind : 'clear';
  }
  return planetWeather(state, planetIndex, actionSeconds);
}

/** 62.54 step 1: the weather's effect at a place (clear when the match has no weather) — `weatherEffect` at a place. */
export function weatherEffectHere(state: Pick<GameState, 'config' | 'turnNumber' | 'planets'>, planetIndex: number, dir: Vec3, actionSeconds = 0): WeatherEffect {
  if (state.config.weather && state.config.weatherCells && !state.config.weatherFixed) return cellWeatherEffect(state, planetIndex, dir, actionSeconds);   // 62.54 step 5: blended across a cell's edge
  return WEATHER_EFFECTS[weatherHere(state, planetIndex, dir, actionSeconds) ?? 'clear'];
}
